import { useCreateAccount as useCreateAccountApi } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  GlucoseUnit,
  CoreConsentsPayload,
} from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import { usePolyglot } from "src/contexts"
import { noticeError } from "src/utils"

interface Params {
  token: string;
  email: string;
  password: string;
  country: string;
  units: GlucoseUnit;
  firstname: string;
  lastname: string;
  dateOfBirth: string;
  nickname: string | null;
  emailConfirmationCode: string;
  consents?: CoreConsentsPayload;
}

// this token allows account creation from localhost in the development and release envs
const turnstileToken = process.env.REACT_APP_TURNSTILE_TOKEN ?? ""

export function useCreateAccount() {
  const createAccount = useCreateAccountApi()
  const polyglot = usePolyglot()

  return useMemo(
    () => !createAccount
      ? undefined
      : async (params: Params) => {
        const timezone = `${dayjs().utcOffset() / 60}` // utc offset in hours
        const language = polyglot.locale()

        try {
          const response = await createAccount({
            token: process.env.NODE_ENV === "development" && turnstileToken
              ? turnstileToken
              : params.token,
            account: {
              language,
              timezone,
              email: params.email,
              password: params.password,
              country: params.country,
              units: params.units,
              firstname: params.firstname,
              lastname: params.lastname,
              birthdate: params.dateOfBirth,
            },
            follow: {
              nickname: params.nickname,
            },
            email_confirmation: {
              code: params.emailConfirmationCode,
            },
            consents: params.consents,
          })

          return response
        } catch (e) {
          noticeError(
            e,
            "CreateAccount",
            {
              language,
              timezone,
              email: params.email,
              country: params.country,
              units: params.units,
              firstname: params.firstname,
              lastname: params.lastname,
              birthdate: params.dateOfBirth,
              nickname: params.nickname,
              consents: params.consents,
            },
          )

          throw e
        }
      },
    [
      createAccount,
      polyglot,
    ],
  )
}
